/**
 * HexClusterView — one group's header + its honeycomb of CameraHex tiles.
 * Positions are absolute canvas-space coords from layoutSites().
 *
 * The header is a COLUMN HEADING, not a label: name + the group's one-line state on the
 * top row, where it lands next to its neighbours' headings and can be compared across the
 * panel; the location and camera count sit under it in the quieter second line; a rule
 * closes the block so the hexes below read as belonging to it. That rule is what stops a
 * wide panel of groups from looking like one undifferentiated field of hexagons.
 */
import CameraHex from './CameraHex';
import { TITLE_H, type LaidOutCluster, type PositionedHex } from './hexLayout';
import { summarize, summaryBadge } from './hexSummary';
import type { HexColorState } from './types';
import type { HexCamera, HexCounter } from '../../api/contracts';

interface HexClusterViewProps {
  cluster: LaidOutCluster<HexCamera>;
  selectedId: string | null;
  colorStateFor: (camera: HexCamera) => HexColorState;
  counterFor: (camera: HexCamera) => HexCounter | undefined;
  liveFor: (camera: HexCamera) => boolean;
  recordingFor: (camera: HexCamera) => boolean;
  /** Filtered OUT by the toolbar — drawn as a ghost so the layout never moves. */
  isDimmed?: (camera: HexCamera) => boolean;
  onSelect: (hex: PositionedHex<HexCamera>) => void;
  onHoverStart: (hex: PositionedHex<HexCamera>) => void;
  onHoverEnd: () => void;
}

export default function HexClusterView({
  cluster,
  selectedId,
  colorStateFor,
  counterFor,
  liveFor,
  recordingFor,
  isDimmed,
  onSelect,
  onHoverStart,
  onHoverEnd,
}: HexClusterViewProps) {
  const summary = summarize(
    cluster.hexes.map((h) => h.item),
    { colorStateFor, counterFor },
  );
  const badge = summaryBadge(summary);

  return (
    <>
      <div
        data-tour="alert-board-group"
        className="absolute flex select-none flex-col justify-end pb-1.5"
        style={{ left: cluster.originX, top: cluster.originY, width: cluster.width, height: TITLE_H }}
      >
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[12px] font-bold uppercase tracking-wider text-foreground">
            {cluster.title}
          </span>
          <span className={`flex-none rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${badge.className}`}>
            {badge.label}
          </span>
        </div>
        <span className="truncate text-[9px] font-medium uppercase tracking-wider text-muted-foreground">
          {cluster.subtitle ? `${cluster.subtitle} · ` : ''}
          {summary.total} camera{summary.total === 1 ? '' : 's'}
        </span>
        <div className="mt-1 h-px w-full bg-border" />
      </div>

      {cluster.hexes.map((hex) => (
        <CameraHex
          key={hex.item.id}
          name={hex.item.name}
          colorState={colorStateFor(hex.item)}
          counter={counterFor(hex.item)}
          live={liveFor(hex.item)}
          recording={recordingFor(hex.item)}
          x={hex.x}
          y={hex.y}
          selected={selectedId === hex.item.id}
          dimmed={isDimmed?.(hex.item)}
          onClick={() => onSelect(hex)}
          onHoverStart={() => onHoverStart(hex)}
          onHoverEnd={onHoverEnd}
        />
      ))}
    </>
  );
}
